import { memo, useEffect, useMemo, useState } from "react"
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import { unstable_batchedUpdates } from "react-dom"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import api from "../../api"
import { SearchBar } from "../../components/Tables/Columns"
import { countryNamesInEnglish, dateFormatter } from "../../utils/commonFunctions"

const LineHaulBagsTableExpandedRow = (props: any) => {
    const { t } = useTranslation()

    const [loading, setLoading] = useState<boolean>(false)
    const [bag, setBag] = useState<{ parcels: []; remarks: string } | undefined>(undefined)
    const [error, setError] = useState<string>("")

    const columns: ColumnDescription<any, any>[] = useMemo(
        () => [
            {
                text: t("Shipment ID"),
                dataField: "uuid",
                classes: "highlighted-text"
            },
            {
                text: t("Account ID"),
                dataField: "user",
                classes: "highlighted-text",
                formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                    return cell?.user_pin ? cell?.user_pin : "N/A"
                }
            },
            {
                text: t("Date"),
                dataField: "created_at",
                formatter: (cell: string, row: Record<string, any>) => {
                    let date
                    if (cell && cell?.length > 0) {
                        date = dateFormatter(cell, false, "")
                    }
                    return cell && cell?.length > 0 ? (
                        <>
                            <span className="d-block">{date}</span>
                        </>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("A. Weight & Dimensions"),
                dataField: "a-weight",
                formatter: (cell: any, row: Record<string, any>) => (
                    <>
                        {row.weight_unit && typeof row.weight_value === "number" ? (
                            <>
                                <span className="fw-bold">{row.weight_value}</span>
                                &nbsp;
                                <span>{row.weight_unit}</span>
                            </>
                        ) : (
                            "-"
                        )}

                        <br />
                        {typeof row.dimension_length === "number" &&
                        typeof row.dimension_width === "number" &&
                        typeof row.dimension_height === "number" &&
                        row.dimensions_unit ? (
                            <>
                                {`${row.dimension_length}`}&#x2715;
                                {`${row.dimension_width}`}&#x2715;
                                {`${row.dimension_height}`}&nbsp;
                                {row.dimensions_unit}
                            </>
                        ) : (
                            "-"
                        )}
                    </>
                )
            },
            {
                text: t("Destination"),
                dataField: "ParcelAddress",
                formatter: (_cell: any, row: Record<string, any>) => {
                    const cell = _cell?.code
                    return cell && cell !== "" && countryNamesInEnglish.of(cell) ? (
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={
                                <Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>
                            }>
                            <>
                                <Flag
                                    className="d-inline-block mx-1"
                                    style={{ width: 28, height: 18 }}
                                    code={(cell ?? "").toLowerCase()}
                                />
                                &nbsp;{cell}
                            </>
                        </OverlayTrigger>
                    ) : (
                        "-"
                    )
                }
            },
            {
                text: t("Value"),
                dataField: "amount",
                formatter: (cell: number, row: Record<string, any>) => {
                    return cell && cell > 0 ? `$${cell}` : "-"
                }
            },
            {
                text: t("Type"),
                dataField: "cod",
                formatter: (cell: number, row: Record<string, any>) => {
                    return cell === 1 ? "COD" : "Prepaid"
                }
            },
            {
                text: t("Terms"),
                dataField: "terms",
                formatter: (cell: number, row: Record<string, any>) => {
                    return "DDU"
                }
            },
            {
                text: t("LM AWB"),
                dataField: "new_awb_number",
                formatter: (cell: string, row: Record<string, any>) => {
                    return cell?.length > 0 ? cell : "-"
                }
            },
            {
                text: t("TBA"),
                dataField: "purchase_tracking_number",
                formatter: (cell: string, row: Record<string, any>) => {
                    return cell?.length > 0 ? cell : "-"
                }
            }
        ],
        [t]
    )

    useEffect(() => {
        const getBag = async (bagId: string) => {
            try {
                setLoading(true)
                const response = await api.linehaul.bagDetails({
                    bagId
                })
                if (!response?.data?.status || !response.data.payload)
                    throw new Error(response?.data?.message ?? "Unable to get bag data")
                unstable_batchedUpdates(() => {
                    setBag(response.data.payload)
                    setError("")
                    setLoading(false)
                })
            } catch (e: any) {
                unstable_batchedUpdates(() => {
                    setBag(undefined)
                    setError(e.message ?? "Unable to get bag data")
                    setLoading(false)
                })
            }
        }
        getBag(props.bag.bag_id)
    }, [props.bag.bag_id])

    if (loading)
        return (
            <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only"></span>
                </div>
            </div>
        )

    if (error || !bag) return <p className="text-danger text-center">{error ?? "Unable to get bag data"}</p>

    return (
        <>
            <div className="table-responsive mb-3">
                {columns && (
                    <ToolkitProvider
                        keyField="id"
                        data={bag?.parcels ?? []}
                        columns={columns}
                        search={{ searchFormatted: true }}>
                        {(props) => (
                            <div className="table-container my-4 container-fluid ">
                                <Row className="search suites-search">
                                    <Col xs={12} sm={12} md={12} lg={12} className="d-flex justify-content-end">
                                        <SearchBar {...props.searchProps} />
                                    </Col>
                                </Row>
                                <Row className="my-3">
                                    <Col>
                                        <div
                                            style={{
                                                maxHeight: "200px",
                                                overflow: "auto"
                                            }}>
                                            <BootstrapTable
                                                {...props.baseProps}
                                                wrapperClasses="table-wrapper my-3 "
                                                headerClasses="header-class"
                                                bordered={false}
                                                noDataIndication={t("No_Record")}
                                                filter={filterFactory()}
                                                rowClasses="custom-table-row"
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </ToolkitProvider>
                )}
            </div>

            <Form.Group className="mb-3 w-100">
                <Form.Label htmlFor="remarks">{t("Remarks")}</Form.Label>
                <Form.Control
                    value={bag.remarks}
                    as="textarea"
                    name={"remarks"}
                    autoComplete="off"
                    placeholder="Remarks..."
                    disabled
                />
            </Form.Group>
        </>
    )
}

export default memo(LineHaulBagsTableExpandedRow)
